/**
 * FDV — estados de una landing de campaña por vigencia (`sections/fdv-promo-campaign.liquid`).
 *
 * Una sola regla, escrita dos veces a propósito con el MISMO contrato:
 *   - Liquid (server) decide el estado inicial y escribe el <style> que oculta
 *     lo que no corresponde → correcto sin JS y sin parpadeo.
 *   - Este módulo lo recalcula en el navegador para que la página abierta cambie
 *     sola en la frontera (6:00 pm, 3:00 am, 12:00 am…) y para previsualizar (`?promo_at=`).
 *
 * Estados: soon (antes de `start`) · day (vigente, sin ventana nocturna activa,
 * aún faltan ventanas) · night (dentro de una ventana) · closing (vigente, ya
 * pasó la última ventana) · ended (desde `end`, o antes de `announce` si existe:
 * campaña aún no anunciada). Mercado Nocturno 2026, reapertura: visible desde el
 * 30-sep 12:00 am; ventanas 6:00 pm → 3:00 am del 30-sep y 1-oct (cierra el viernes 2 a las 3:00 am).
 *
 * Todo son INSTANTES absolutos (ISO con offset explícito, p. ej.
 * `2026-09-24T18:00:00-06:00`) → no depende de la zona horaria del dispositivo.
 * La landing solo COMUNICA: Shopify gobierna la vigencia real del descuento.
 *
 * Sin imports ni observers a propósito: puro y comprobable en Node, y sin riesgo
 * del bucle de MutationObserver del postmortem del 21-sep (doc 26).
 */

const MAX_TIMEOUT = 2 ** 31 - 1;
const STATES = ['soon', 'day', 'night', 'closing', 'ended'];
const HAS_OFFSET = /(?:[zZ]|[+-]\d\d:?\d\d)$/;

/** ISO con offset → ms epoch. NaN si no es un instante explícito válido. */
export function parseInstant(raw) {
  const s = String(raw ?? '').trim();
  if (!HAS_OFFSET.test(s)) return NaN; // sin offset = ambiguo (zona del dispositivo) → se rechaza
  return Date.parse(s);
}

/** Texto multilínea `inicio/fin` (fin exclusivo) → [{start,end}] en ms, descartando líneas inválidas. */
export function parseWindows(raw) {
  return String(raw ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [a, b] = line.split('/');
      return { start: parseInstant(a), end: parseInstant(b) };
    })
    .filter((w) => Number.isFinite(w.start) && Number.isFinite(w.end) && w.end > w.start);
}

/** cfg = { start, end, announce?, windows:[{start,end}] } (ms). Fin de ventana y de campaña = exclusivos. */
export function computeState(now, cfg) {
  const { start, end, announce, windows = [] } = cfg;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 'ended'; // config rota → falla seguro: sin mensajes de promo
  if (Number.isFinite(announce) && now < announce) return 'ended'; // aún no se anuncia → se ve como cerrada
  if (now < start) return 'soon';
  if (now >= end) return 'ended';
  if (windows.some((w) => now >= w.start && now < w.end)) return 'night';
  const lastEnd = windows.reduce((m, w) => Math.max(m, w.end), 0);
  if (lastEnd && now >= lastEnd) return 'closing';
  return 'day';
}

/** Próximo instante en que el estado puede cambiar (ms) o null. */
export function nextBoundary(now, cfg) {
  const points = [cfg.announce, cfg.start, cfg.end, ...(cfg.windows || []).flatMap((w) => [w.start, w.end])];
  const future = points.filter((p) => Number.isFinite(p) && p > now);
  return future.length ? Math.min(...future) : null;
}

/** Regla que oculta todo lo etiquetado `data-fdv-promo-only` que NO incluya el estado vigente. */
export function cssFor(state) {
  return `[data-fdv-promo-only]:not([data-fdv-promo-only~="${state}"]){display:none!important}`;
}

/** Fecha legible siempre en la zona de la tienda (solo para el aviso de vista previa). */
function formatMx(ms) {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      timeZone: 'America/Mexico_City',
      dateStyle: 'full',
      timeStyle: 'short',
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString();
  }
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class FdvPromoState extends HTMLElement {
    #timer = 0;
    #cfg = null;
    #simulated = false;

    connectedCallback() {
      const d = this.dataset;
      const windows = parseWindows(d.windows);
      const start = parseInstant(d.start);
      const end = parseInstant(d.end);
      const announce = parseInstant(d.announce); // vacío o ilegible → NaN → sin restricción (igual que Liquid)
      this.#cfg = { start, end, announce, windows };

      const serverState = STATES.includes(d.state) ? d.state : null;
      const forcedByEditor = d.forced === 'true';
      const override = d.allowPreview === 'false' ? NaN : this.#readOverride(start);

      if (Number.isFinite(override)) {
        // Vista previa por URL: instante fijo (no avanza), con aviso visible.
        this.#simulated = true;
        this.#apply(computeState(override, this.#cfg), { note: `Vista previa: simulando ${formatMx(override)} (hora Ciudad de México).` });
        return;
      }
      if (forcedByEditor) {
        // Estado forzado desde el editor: el server ya lo aplicó. Solo lo reflejamos.
        this.#setRootAttr(serverState);
        return;
      }

      // El reloj del dispositivo puede estar mal: si difiere >6 h del server, mandamos el estado del server.
      const serverNow = Number(d.serverNow) * 1000;
      const skewed = Number.isFinite(serverNow) && Math.abs(Date.now() - serverNow) > 6 * 3600 * 1000;
      if (skewed) {
        this.#setRootAttr(serverState);
        return;
      }

      this.#tick();
      document.addEventListener('visibilitychange', this.#onVisible);
    }

    disconnectedCallback() {
      clearTimeout(this.#timer);
      document.removeEventListener('visibilitychange', this.#onVisible);
    }

    #onVisible = () => {
      if (!document.hidden && !this.#simulated) this.#tick();
    };

    /** Recalcula con el reloj actual y agenda la próxima frontera (los timers se frenan en pestañas ocultas → visibilitychange). */
    #tick() {
      clearTimeout(this.#timer);
      const now = Date.now();
      this.#apply(computeState(now, this.#cfg));
      const next = nextBoundary(now, this.#cfg);
      if (next != null) {
        this.#timer = setTimeout(() => this.#tick(), Math.min(next - now + 250, MAX_TIMEOUT));
      }
    }

    #readOverride(start) {
      const raw = new URLSearchParams(location.search).get('promo_at');
      if (!raw) return NaN;
      let v = raw.trim().replace(' ', 'T');
      if (!HAS_OFFSET.test(v)) {
        // Sin offset → se usa el de la campaña (nunca la zona del dispositivo).
        const m = String(this.dataset.start || '').match(/([+-]\d\d:?\d\d|[zZ])$/);
        v += m ? m[1] : '-06:00';
      }
      const t = Date.parse(v);
      return Number.isFinite(t) ? t : NaN;
    }

    #apply(state, { note = '' } = {}) {
      const style = document.getElementById('fdv-promo-state-css');
      const hide = this.dataset.hideSelector;
      const css = cssFor(state) + (hide && state !== 'ended' ? `${hide}{display:none!important}` : '');
      if (style && style.textContent !== css) style.textContent = css;
      this.#setRootAttr(state);
      const banner = this.querySelector('[data-fdv-promo-preview]');
      if (banner) {
        const text = note ? `${note} Estado: «${state}».` : '';
        if (banner.textContent !== text) banner.textContent = text;
        if (banner.hidden !== !note) banner.hidden = !note;
      }
    }

    #setRootAttr(state) {
      const previousState = document.documentElement.dataset.fdvPromoState || null;
      if (state) this.dataset.currentState = state;
      if (state && previousState !== state) {
        document.documentElement.dataset.fdvPromoState = state;
        document.dispatchEvent(new CustomEvent('fdv:promo:state', {
          detail: {
            state,
            previous_state: previousState,
            campaign_id: this.dataset.campaignId || null,
          },
        }));
      }
    }
  }

  if (!customElements.get('fdv-promo-state')) customElements.define('fdv-promo-state', FdvPromoState);
}
